// Lightmap atlas: the map's lightmaps (the BSP's 128x128 ones or q3map2
// -external TGAs) overbright-shifted and packed into a few big pages, so
// surfaces of one shader draw in one batch whatever their lightmap. Each tile
// has a one texel border copied from its edge, so bilinear filtering at the
// tile edge behaves like the clamped single lightmap. No Three.js (worker).

import { LIGHTMAP_SIZE, type Bsp } from '../shared/bsp';
import { colorShift } from './colorShift';
import { decodeTga } from './tga';

/** Overbright shift of the lightmaps (r_mapOverBrightBits 2, no hw gamma). */
const LIGHT_SHIFT = 2;
const PAD = 1;

export interface LightmapImage {
  width: number;
  height: number;
  /** RGBA, already shifted. */
  data: Uint8Array;
}

export interface AtlasTile {
  page: number;
  /** Lightmap uv -> page uv: u0 + u * su, v0 + v * sv. */
  u0: number;
  v0: number;
  su: number;
  sv: number;
}

export interface LightmapLayout {
  pageSize: number;
  /** RGBA pages, pageSize x pageSize. */
  pages: Uint8Array[];
  /** By lightmap number; undefined for a missing lightmap (the surfaces are vertex lit). */
  tiles: (AtlasTile | undefined)[];
}

/** The map's lightmaps as shifted RGBA images (null: an external lightmap is missing). */
export async function loadLightmapImages(bsp: Bsp, mapName: string, read: (path: string) => Promise<Uint8Array | null>): Promise<(LightmapImage | null)[]> {
  const shifted = (rgb: ArrayLike<number>, w: number, h: number, stride: number): LightmapImage => {
    const data = new Uint8Array(w * h * 4);
    for (let i = 0; i < w * h; i++) {
      colorShift(rgb, i * stride, data, i * 4, LIGHT_SHIFT);
      data[i * 4 + 3] = 255;
    }
    return { width: w, height: h, data };
  };
  if (bsp.lightmaps.length) return bsp.lightmaps.map((lm) => shifted(lm, LIGHTMAP_SIZE, LIGHTMAP_SIZE, 3));
  // q3map2 -external lightmaps: maps/<name>/lm_0000.tga
  const maxLm = bsp.surfaces.reduce((m, s) => Math.max(m, s.lightmapNum), -1);
  const out: (LightmapImage | null)[] = [];
  for (let i = 0; i <= maxLm; i++) {
    const data = await read(`maps/${mapName}/lm_${String(i).padStart(4, '0')}.tga`);
    if (!data) {
      out.push(null);
      continue;
    }
    try {
      const img = decodeTga(data);
      out.push(shifted(img.data, img.width, img.height, 4));
    } catch {
      out.push(null);
    }
  }
  return out;
}

/**
 * Shelf packing into pages of at most maxSize (power of two, as small as the
 * lightmaps allow). Null when there is nothing to pack or a lightmap does
 * not fit a page.
 */
export function packLightmaps(images: (LightmapImage | null)[], maxSize = 4096): LightmapLayout | null {
  const present = images.map((img, i) => ({ img, i })).filter((x): x is { img: LightmapImage; i: number } => !!x.img);
  if (!present.length) return null;
  if (present.some(({ img }) => img.width + 2 * PAD > maxSize || img.height + 2 * PAD > maxSize)) return null;
  // the smallest page that holds everything (else full size pages)
  const area = present.reduce((a, { img }) => a + (img.width + 2 * PAD) * (img.height + 2 * PAD), 0);
  let pageSize = 256;
  while (pageSize < maxSize && pageSize * pageSize < area * 1.15) pageSize *= 2;
  // tallest first: tight shelves
  const order = [...present].sort((a, b) => b.img.height - a.img.height || a.i - b.i);
  const tiles: (AtlasTile | undefined)[] = new Array(images.length).fill(undefined);
  const placed: { i: number; page: number; x: number; y: number }[] = [];
  let page = 0;
  let x = 0;
  let y = 0;
  let shelf = 0;
  for (const { img, i } of order) {
    const w = img.width + 2 * PAD;
    const h = img.height + 2 * PAD;
    if (x + w > pageSize) {
      x = 0;
      y += shelf;
      shelf = 0;
    }
    if (y + h > pageSize) {
      page++;
      x = 0;
      y = 0;
      shelf = 0;
    }
    placed.push({ i, page, x, y });
    tiles[i] = { page, u0: (x + PAD) / pageSize, v0: (y + PAD) / pageSize, su: img.width / pageSize, sv: img.height / pageSize };
    x += w;
    shelf = Math.max(shelf, h);
  }
  const pages = Array.from({ length: page + 1 }, () => new Uint8Array(pageSize * pageSize * 4));
  for (const p of placed) {
    const img = images[p.i]!;
    const dst = pages[p.page];
    // with the border: rows and columns -1 .. size clamp to the image's edge
    for (let ty = -PAD; ty < img.height + PAD; ty++) {
      const sy = Math.min(img.height - 1, Math.max(0, ty));
      for (let tx = -PAD; tx < img.width + PAD; tx++) {
        const sx = Math.min(img.width - 1, Math.max(0, tx));
        const s = (sy * img.width + sx) * 4;
        const d = ((p.y + PAD + ty) * pageSize + (p.x + PAD + tx)) * 4;
        dst[d] = img.data[s];
        dst[d + 1] = img.data[s + 1];
        dst[d + 2] = img.data[s + 2];
        dst[d + 3] = img.data[s + 3];
      }
    }
  }
  return { pageSize, pages, tiles };
}
